import cx from "clsx";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import React from "react";

import FigureMintingImage from "./components/FigureMintingImage";
import ProgressBar from "./components/ProgressBar";
import styles from "./index.module.scss";

import { useEffectOnce } from "@/modules/common-hooks/useEffectOnce";
import { EM_DASH } from "@/modules/common-utils/unicode";

type Ratio = number; // 0.0 .. 1.0

type Progress = {
  text: string;
  ratio: Ratio;
  ratioUpTo: Ratio;
  shrinkRatio: Ratio;
};

export type ModalProgress$Progress = Progress;

type Controller<T> = {
  update: (progress: Progress) => void;
  resolve: (result: T) => void;
  reject: (reason: unknown) => void;
};

export type ModalProgress$Controller<T> = Controller<T>;

type ContentProps = {
  className?: string;
  style?: React.CSSProperties;
  progress?: Progress;
  customImage?: React.ReactNode;
};

function Content({ className, style, progress, customImage }: ContentProps) {
  return (
    <ModalV2.Content className={cx(styles.content, className)} style={style}>
      <Flex.Col alignItems="center" gap="32px" justifyContent="center">
        {customImage ? customImage : <FigureMintingImage />}
        <div className={styles.textStep}>{progress?.text || EM_DASH}</div>
        <ProgressBar
          style={{ minWidth: "500px" }}
          {...(progress
            ? {
                ratio: progress.ratio,
                ratioUpTo: progress.ratioUpTo,
                shrinkRatio: progress.shrinkRatio,
              }
            : {
                ratio: 0,
                ratioUpTo: 0,
                shrinkRatio: 1,
              })}
        />
      </Flex.Col>
    </ModalV2.Content>
  );
}

type RootProps<T> = {
  open: boolean;
  task: (controller: Controller<T>) => void;
  onResolve: (result: T) => void;
  onReject: (reason: unknown) => void;
};

function Root<T>({ open, task, onResolve, onReject }: RootProps<T>) {
  const [progress, setProgress] = React.useState<Progress>();

  useEffectOnce(() => {
    task({
      update: setProgress,
      resolve: onResolve,
      reject: onReject,
    });
  });

  return (
    <ModalV2
      open={open}
      onClose={() => onReject(null)}
      closeOnDimmerClick={false}
      closeOnEscape={false}
    >
      <Content className={styles.content} progress={progress} />
    </ModalV2>
  );
}

const ModalProgress = Object.assign(Root, { Root, Content });

export default ModalProgress;
